import { useState, useEffect, useMemo } from "react";
import MenuItemsTable from "../components/common/menu-items-table";
import MenuItemEditPanel from "../components/common/menu-item-edit-panel";
import MenuAnalyticsModal from "../components/common/menu-analytics-modal";
import Button from "../components/common/button";
import WineItemEditPanel from "../components/common/wine-item-edit-panel";
import BeerItemEditPanel from "../components/common/beer-item-edit-panel";
import Dropdown from "../components/common/dropdown";
import { MenuService } from "../services/MenuService";
import { Upload, BarChart2, Plus, Search } from "lucide-react";
import * as XLSX from "xlsx";
import { RestaurantsService } from "../services/Restaurants";
import { CORE_STYLES } from "../utils/beerTaxonomy";

export default function MenuManagement() {
  const [activeTab, setActiveTab] = useState("food");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedLocation, setSelectedLocation] = useState("All Locations");
  const [selectedDateFilter, setSelectedDateFilter] = useState("Date Added");
  const [currentMenu, setCurrentMenu] = useState("Current Menu");
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [wines, setWines] = useState([]);
  const [beers, setBeers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(Number(newLimit));
  };
  // Cache for storing fetched pages: { "food-1": { dishes: [], totalPages: 1, totalItems: 0 }, ... }
  const [pageCache, setPageCache] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("All Countries");
  const [selectedRegion, setSelectedRegion] = useState("All Regions");
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [excelPreviewData, setExcelPreviewData] = useState(null);
  const [excelPreviewHeaders, setExcelPreviewHeaders] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [validDishTypes, setValidDishTypes] = useState([]);
  const [validWineTypes, setValidWineTypes] = useState([]);
  const [validBeerTypes, setValidBeerTypes] = useState([]);
  const [foodCategoriesOptions, setFoodCategoriesOptions] = useState([]);
  const [wineCategoriesOptions, setWineCategoriesOptions] = useState([]);
  const [beerCategoriesOptions, setBeerCategoriesOptions] = useState([]);
  const [isFileValid, setIsFileValid] = useState(false);
  const [excelPreviewDataSample, setExcelPreviewDataSample] = useState(null);
  const [excelPreviewHeadersSample, setExcelPreviewHeadersSample] = useState(
    []
  );
  const [filters, setFilters] = useState({
    restaurant: null,
    category: null,
    status: null,
  });
  const [wineOffering, setWineOffering] = useState({ btg: false, btb: false });
  const [beerOffering, setBeerOffering] = useState({ draft: false, can: false, bottle: false });
  const [wineAnalytics, setWineAnalytics] = useState(null);
  const userRole = localStorage.getItem("userRole");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null); // null, 'asc', 'desc'
  const [dishTypes, setDishTypes] = useState([]);


  const allowedHeaders = [
    "Dish Name (exact as it appears on your menu)",
    "Dish Description (as shown on your menu)",
    "Dish Course (choose from category options)",
    "Price (numbers only, no $)",
    "Main Ingredients (comma separated)",
    "Allergens Included (select all that apply)",
    "Health Restrictions Included (select all that apply)",
    "Belief Accommodations (select all that apply)",
    "Lifestyle Accommodations (select all that apply)",
    "Dish Accommodations (select all that apply)",
    "Can Ingredients Be Substituted? (Yes/No)",
    "Available Substitutions",
    "Additional Notes (optional)",
    "Cross Contact Possible? (Yes/No)",
  ];

  const allowedWineHeaders = [
    "Producer Name (Winery)",
    "Product Name (Label Name)",
    "Grape Varietal(s)",
    "Country of Origin",
    "Region",
    "Sub-Region (if applicable)",
    "Appellation / Commune",
    "Vineyard Name (if applicable)",
    "Vintage (Year)",
    "Wine Category (choose from select options)",
    "Filtered? (Yes/No)",
    "Has Residual Sugar? (Yes/No)",
    "Organic (Yes/No)",
    "Biodynamic (Yes/No)",
    "Vegan (Yes/No)",
    "Offered by the Glass (Yes/No)",
    "Offered by the Bottle (Yes/No)",
    "Glass Price (numbers only, no $)",
    "Bottle Price (numbers only, no $)",
    "Wine Style (choose one from select options)",
    "Tasting Notes / Additional Details",
  ];

  // Matches the Beer "Download Template" file (public/beer.xlsx).
  const allowedBeerHeaders = [
    "Brewery Name",
    "Product Name",
    "Beer Style",
    "ABV (%)",
    "IBU",
    "Brewery City",
    "State / Province / Territory",
    "Country of Origin",
    "Imported? (Yes/No)",
    "Filtered? (Yes/No)",
    "Non-Alcoholic? (Yes/No)",
    "Contains Gluten? (Yes/No)",
    "Contains Dairy / Lactose? (Yes/No)",
    "Contains Nuts? (Yes/No)",
    "Draft Serving Sizes",
    "Draft Prices",
    "Can Serving Sizes",
    "Can Prices",
    "Bottle Serving Sizes",
    "Bottle Prices",
    "Beer Description",
    "Seasonal? (Yes/No)",
    "Limited Release? (Yes/No)",
    "Beer Label Image URL",
    "Training Notes / Additional Details",
  ];

  const filterOptions = {
    restaurant: [
      { value: "all", label: "All Restaurants" },
      ...restaurants.map((restaurant) => ({
        value: restaurant.uuid,
        label: restaurant.name,
      })),
    ],
  };

  const currentRestaurantName = useMemo(() => {
    if (!filters.restaurant || filters.restaurant === "all") return "All Restaurants";
    const restaurant = restaurants.find(r => (r.uuid || r.id) === filters.restaurant);
    return restaurant ? restaurant.name : "All Restaurants";
  }, [filters.restaurant, restaurants]);

  // Initial load effect - runs only once
  useEffect(() => {
    if (isInitialLoad) {
      setCurrentPage(1);
      fetchData(activeTab, 1, true);
      setIsInitialLoad(false);
    }
  }, []);

  // Tab change effect - runs when tab changes (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
      fetchData(activeTab, 1, false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
      fetchData(activeTab, 1, false);
    }
  }, [itemsPerPage]);

  // Search query effect - refetch data when search changes
  useEffect(() => {
    if (!isInitialLoad) {
      const debounceId = setTimeout(() => {
        fetchData(activeTab, 1, false);
      }, 300);

      return () => clearTimeout(debounceId);
    }
  }, [searchQuery, filters, wineOffering, beerOffering]);

  // Sort effect - immediate fetch without debounce (runs on any sort change)
  useEffect(() => {
    if (!isInitialLoad) {
      // Reset to first page already done in handler, ensure we fetch page 1
      console.log('fetch data running....')
      fetchData(activeTab, 1, false);
    }
  }, [sortColumn, sortDirection, activeTab, isInitialLoad]);

  useEffect(() => {
    // if (showBulkUploadModal) {
    RestaurantsService.getAllRestaurants('name')
      .then((data) => setRestaurants(data.restaurants || data))
      .catch(() => setRestaurants([]));
    setSampleData();
    // }
  }, []);

  useEffect(() => {
    if (activeTab === "wine" && userRole === "employee") {
      const restaurantUuid = filters.restaurant === "all" ? null : filters.restaurant;
      MenuService.getWineAnalytics(restaurantUuid)
        .then((data) => setWineAnalytics(data))
        .catch((err) => console.error("Error fetching wine analytics:", err));
    }
    // The bulk upload format sample depends on the active tab.
    setSampleData();
  }, [activeTab, userRole, filters.restaurant]);

  // Trigger training stats recalculation for the selected restaurant
  useEffect(() => {
    if (filters.restaurant && filters.restaurant !== "all" && (userRole === "manager" || userRole === "director" || userRole === "super_admin")) {
      const timer = setTimeout(() => {
        MenuService.recalculateTrainingStats(filters.restaurant)
          .then(() => {
            console.log("Training stats recalculated successfully");
            // Refresh current data to show updated stats
            fetchData(activeTab, 1, false);
          })
          .catch((err) => console.error("Error recalculating training stats:", err));
      }, 2000); // 2 second delay to prioritize main content loading

      return () => clearTimeout(timer);
    }
  }, [filters.restaurant, userRole]);

  // Set default filters for Employee
  useEffect(() => {
    if (userRole === "employee" && restaurants.length > 0) {
      setFilters((prev) => ({
        ...prev,
        restaurant: restaurants[0].uuid || restaurants[0].id,
        status: "active",
      }));
    }
  }, [userRole, restaurants]);

  const setSampleData = () => {
    if (activeTab === "food") {
      setExcelPreviewHeadersSample(allowedHeaders);
      setExcelPreviewDataSample([
        [
          "Margherita Pizza", // Dish Name
          "Classic pizza with tomato and mozzarella", // Dish Description
          "Main Course", // Dish Course
          "12.99", // Price
          "Tomato, Mozzarella, Basil", // Main Ingredients
          "Dairy", // Allergens Included
          "Gluten-Free, Low Sodium", // Health Restrictions Included
          "Halal", // Belief Accommodations
          "Vegan, Vegetarian", // Lifestyle Accommodations
          "Extra cheese, No onions", // Dish Accommodations
          "Yes", // Can Ingredients Be Substituted?
          "Cheese, Sauce", // Available Substitutions
          "Freshly baked, best served hot", // Additional Notes (optional)
          "No", // Cross Contact Possible? (Yes/No)
        ],
      ]);
    } else if (activeTab === "beer") {
      setExcelPreviewHeadersSample(allowedBeerHeaders);
      setExcelPreviewDataSample([
        [
          "Sierra Nevada",
          "Pale Ale",
          "Pale Ale (American)",
          "5.6",
          "38",
          "Chico",
          "California",
          "USA",
          "No",
          "Yes",
          "No",
          "Yes",
          "No",
          "No",
          "12oz, 16oz",
          "5, 7",
          "12oz",
          "4",
          "22oz",
          "9",
          "Classic pale ale with piney hops and a clean finish.",
          "No",
          "No",
          "",
          "Great with burgers and grilled chicken",
        ],
      ]);
    } else {
      setExcelPreviewHeadersSample(allowedWineHeaders);
      setExcelPreviewDataSample([
        [
          "Chateau Margaux",
          "Grand Vin",
          "Cabernet Sauvignon",
          "France",
          "Bordeaux",
          "Margaux",
          "Appellation Margaux",
          "Margaux Vineyard",
          "2018",
          "Red",
          "No",
          "No",
          "Yes",
          "No",
          "Yes",
          "Yes",
          "Yes",
          "25",
          "120",
          "Full-bodied",
          "Excellent vintage with rich tannins and complex flavors",
        ],
      ]);
    }
  };

  const fetchData = async (tab, page = 1, isInitial = false, isItemAdd = false) => {
    const trimmedQuery = (searchQuery || '').trim();
    const restaurantId = (filters?.restaurant || '').trim();
    const dishType = (filters?.category || '').trim();
    const status = userRole === "employee" ? "active" : filters?.status;
    const btg = wineOffering.btg;
    const btb = wineOffering.btb;
    const draft = tab === "beer" ? beerOffering.draft : undefined;
    const can = tab === "beer" ? beerOffering.can : undefined;
    const bottle = tab === "beer" ? beerOffering.bottle : undefined;
    const normalizedQueryKey = trimmedQuery.toLowerCase();
    // Cache key must include filters AND sort to prevent showing wrong cached data
    const cacheKey = `${tab}-${page}-${itemsPerPage}-${normalizedQueryKey}-${restaurantId}-${dishType}-${status}-${btg}-${btb}-${draft}-${can}-${bottle}-${sortColumn}-${sortDirection}`;


    // Check if data is already cached
    if (pageCache[cacheKey] && !isItemAdd) {
      const cachedData = pageCache[cacheKey];
      if (tab === "food") {
        setDishes(cachedData.dishes || []);
        setTotalPages(cachedData.totalPages || 1);
        setTotalItems(cachedData.totalItems || 0);
      } else if (tab === "wine") {
        console.log('cacheData', cachedData.wines)
        setWines(cachedData.wines || []);
        setTotalPages(cachedData.totalPages || 1);
        setTotalItems(cachedData.totalItems || 0);
      } else if (tab === "beer") {
        setBeers(cachedData.beers || []);
        setTotalPages(cachedData.totalPages || 1);
        setTotalItems(cachedData.totalItems || 0);
      }
      // No loader needed for cached data
      setInitialLoading(false);
      setTableLoading(false);
      return;
    }

    try {
      // Set appropriate loading state BEFORE clearing arrays
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setTableLoading(true);
        // Clear the current tab's data to show loading state
        if (tab === "food") {
          setDishes([]);
        } else if (tab === "wine") {
          setWines([]);
        } else if (tab === "beer") {
          setBeers([]);
        }
      }

      if (tab === "food") {

        const dishesData = await MenuService.getAllDishes(page, itemsPerPage, trimmedQuery, restaurantId || undefined, dishType || undefined, status || undefined, sortColumn, sortDirection);

        // Robust data extraction: prioritize populated arrays
        const d1 = dishesData?.dishesWithRestaurant;
        const d2 = dishesData?.dishes;
        const d3 = dishesData?.data;

        let dishesList = [];
        if (Array.isArray(dishesData)) {
          dishesList = dishesData;
        } else if (Array.isArray(d1) && d1.length > 0) {
          dishesList = d1;
        } else if (Array.isArray(d2) && d2.length > 0) {
          dishesList = d2;
        } else if (Array.isArray(d3) && d3.length > 0) {
          dishesList = d3;
        } else {
          dishesList = d1 || d2 || d3 || [];
        }

        const totalPagesCount = dishesData?.pagination?.totalPages || 1;
        const totalItemsCount = dishesData?.pagination?.total || dishesData?.pagination?.totalCount || dishesList.length || 0;

        console.log(`Fetched dishes. Query: "${trimmedQuery}". Items found:`, dishesList.length);

        setDishes(dishesList);
        setValidDishTypes(dishesData?.validDishTypes || []);
        // Prepend "All Categories" to options
        setFoodCategoriesOptions(
          [{ value: "all", label: "All Categories" }].concat(
            (dishesData?.validDishTypes || [])?.map((type) => ({
              value: type,
              label: type,
            })) || []
          )
        );

        setTotalPages(totalPagesCount);
        setTotalItems(totalItemsCount);

        setPageCache(prev => ({
          ...prev,
          [cacheKey]: {
            dishes: dishesList,
            totalPages: totalPagesCount,
            totalItems: totalItemsCount,
            query: trimmedQuery,
          }
        }));
      } else if (tab === "wine") {
        console.log('wine block runinng')
        const winesData = await MenuService.getAllWines(page, itemsPerPage, trimmedQuery, restaurantId || undefined, dishType || undefined, status || undefined, btg, btb, sortColumn, sortDirection);

        // Robust data extraction for wines: prioritize populated arrays
        const w1 = winesData?.winesWithRestaurant;
        const w2 = winesData?.wines;
        const w3 = winesData?.data;

        let winesList = [];
        if (Array.isArray(winesData)) {
          winesList = winesData;
        } else if (Array.isArray(w1) && w1.length > 0) {
          winesList = w1;
        } else if (Array.isArray(w2) && w2.length > 0) {
          winesList = w2;
        } else if (Array.isArray(w3) && w3.length > 0) {
          winesList = w3;
        } else {
          winesList = w1 || w2 || w3 || [];
        }

        const totalPagesCount = winesData?.pagination?.totalPages || 1;
        const totalItemsCount = winesData?.pagination?.total || winesData?.pagination?.totalCount || winesList.length || 0;

        console.log(`Fetched wines. Query: "${trimmedQuery}". Items found:`, winesList.length);

        console.log('winelist', winesList)
        setWines(winesList);
        setValidWineTypes(winesData?.wineCategories || []);
        setWineCategoriesOptions(
          [{ value: "all", label: "All Categories" }].concat(
            (winesData?.wineCategories || [])?.map((type) => ({
              value: type,
              label: type,
            })) || []
          )
        );

        setTotalPages(totalPagesCount);
        setTotalItems(totalItemsCount);

        setPageCache(prev => ({
          ...prev,
          [cacheKey]: {
            wines: winesList,
            totalPages: totalPagesCount,
            totalItems: totalItemsCount,
            query: trimmedQuery,
          }
        }));
      } else if (tab === "beer") {
        const beersData = await MenuService.getAllBeers(page, itemsPerPage, trimmedQuery, restaurantId || undefined, dishType || undefined, status || undefined, draft, can, bottle, sortColumn, sortDirection);

        // Robust data extraction for beers: prioritize populated arrays
        const b1 = beersData?.beersWithRestaurant;
        const b2 = beersData?.beers;
        const b3 = beersData?.data;

        let beersList = [];
        if (Array.isArray(beersData)) {
          beersList = beersData;
        } else if (Array.isArray(b1) && b1.length > 0) {
          beersList = b1;
        } else if (Array.isArray(b2) && b2.length > 0) {
          beersList = b2;
        } else if (Array.isArray(b3) && b3.length > 0) {
          beersList = b3;
        } else {
          beersList = b1 || b2 || b3 || [];
        }

        const totalPagesCount = beersData?.pagination?.totalPages || 1;
        const totalItemsCount = beersData?.pagination?.total || beersData?.pagination?.totalCount || beersList.length || 0;

        setBeers(beersList);
        setValidBeerTypes(CORE_STYLES);
        setBeerCategoriesOptions(
          [{ value: "all", label: "All Styles" }].concat(
            (beersData?.beerCategories || CORE_STYLES).map((type) => ({
              value: type,
              label: type,
            }))
          )
        );

        setTotalPages(totalPagesCount);
        setTotalItems(totalItemsCount);

        setPageCache(prev => ({
          ...prev,
          [cacheKey]: {
            beers: beersList,
            totalPages: totalPagesCount,
            totalItems: totalItemsCount,
            query: trimmedQuery,
          }
        }));
      }
      setError(null);
    } catch (err) {
      setError("Failed to fetch menu data");
      console.error("Error fetching menu data:", err);
    } finally {
      setInitialLoading(false);
      setTableLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setSelectedCategory("All Categories");
    // Category options differ per tab (food/wine/beer), so a carried-over
    // value would filter out everything on the new tab.
    setFilters((prev) => (prev.category === null ? prev : { ...prev, category: null }));
    setActiveTab(tab);
    setCurrentPage(1);
    // Don't clear cache when switching tabs - let it use cache if available
    // Cache will be used in fetchData if data exists
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchData(activeTab, newPage, false);
  };

  const handleRefresh = (page = currentPage) => {
    setPageCache({});
    fetchData(activeTab, page, false, true);
  };

  const handleAddItem = () => {
    setSelectedMenuItem(null);
    setShowEditPanel(true);
  };

  const handleEditItem = (item) => {
    setSelectedMenuItem(item);
    setShowEditPanel(true);
  };

  const handleCloseEditPanel = () => {
    setShowEditPanel(false);
    setSelectedMenuItem(null);
  };

  const handleSaveItem = async (item, imageFile) => {
    setIsSaving(true);
    try {
      if (activeTab === "food") {
        if (item && item.uuid) {
          await MenuService.updateDish(item.uuid, item, imageFile);
        } else {
          await MenuService.createDish(item, imageFile);
        }
      } else if (activeTab === "beer") {
        if (item && item.uuid) {
          await MenuService.updateBeer(item.uuid, item, imageFile);
        } else {
          await MenuService.createBeer(item, imageFile);
        }
      } else {
        if (item && item.uuid) {
          await MenuService.updateWine(item.uuid, item, imageFile);
        } else {
          await MenuService.createWine(item, imageFile);
        }
      }
      setShowEditPanel(false);
      setSelectedMenuItem(null);
      // Clear cache to ensure fresh data after save
      setPageCache({});
      console.log("Item saved successfully");
      // Reset to page 1 to ensure we see the new/updated item if sorting puts it there
      setCurrentPage(1);
      handleRefresh(1);
      console.log("items refetched");
    } catch (error) {
      setSaveError(
        error?.response?.data?.message || "An error occurred while saving."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file");
      return;
    }
    if (!selectedRestaurant) {
      setUploadError("Please select a restaurant");
      return;
    }

    const allowedMimeTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const allowedExtensions = [".xls", ".xlsx", ".csv"];
    const fileExtension =
      "." + selectedFile.name.split(".").pop().toLowerCase();

    if (
      !allowedMimeTypes.includes(selectedFile.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      setUploadError("Please select an Excel file (.xls, .xlsx, .csv)");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const bulkUploadType = activeTab === "food" ? "dish" : (activeTab === "beer" ? "beer" : "wine");
      formData.append("restaurant_uuid", selectedRestaurant);
      await MenuService.bulkUploadMenu(formData, bulkUploadType);

      setShowBulkUploadModal(false);
      setSelectedFile(null);
      setExcelPreviewData(null);
      setExcelPreviewHeaders([]);
      setUploadError("");
      setDragActive(false);
      // Clear cache and go to page 1 to see new items
      setPageCache({});
      setCurrentPage(1);
      handleRefresh(1);
    } catch (error) {
      // Show the server's import summary (e.g. which rows failed and why) when it sends one.
      const serverMsg = error?.response?.data?.error || error?.response?.data?.message;
      setUploadError(serverMsg || "Error uploading file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(null);
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      handleFile(file);
    }
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array", codepage: "65001" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false,
        });

        if (json.length > 0) {
          const uploadedHeaders = json[0].map((h) => h?.toString().trim());

          let expectedHeaders =
            activeTab === "food" ? allowedHeaders : (activeTab === "beer" ? allowedBeerHeaders : allowedWineHeaders);
          // For missing headers, make image_url optional
          let missingHeaders = expectedHeaders.filter(
            (header) =>
              header !== "image_url" && !uploadedHeaders.includes(header)
          );
          // For unexpected headers, allow image_url if present
          let allowedExtraHeaders = ["image_url"] || [];
          let unexpectedHeaders = uploadedHeaders.filter(
            (header) =>
              !expectedHeaders.includes(header) &&
              !allowedExtraHeaders.includes(header)
          );

          if (unexpectedHeaders.length > 0 || missingHeaders.length > 0) {
            let errorMsg = "";
            if (unexpectedHeaders.length > 0) {
              errorMsg += `Unexpected column(s): ${unexpectedHeaders.join(
                ", "
              )}. `;
            }
            if (missingHeaders.length > 0) {
              errorMsg += `Missing required column(s): ${missingHeaders.join(
                ", "
              )}.`;
            }

            setUploadError(errorMsg.trim());
            setExcelPreviewData(null);
            setExcelPreviewHeaders([]);
            setIsFileValid(false);
            return;
          }

          // Duplicate name validation for food bulk upload
          if (
            activeTab === "food" &&
            uploadedHeaders.includes(
              "Dish Name (exact as it appears on your menu)"
            )
          ) {
            const nameIndex = uploadedHeaders.indexOf(
              "Dish Name (exact as it appears on your menu)"
            );
            const seenNames = new Set();
            for (let i = 1; i < json.length; i++) {
              const row = json[i];
              const name = row[nameIndex]?.toString().trim().toLowerCase();
              if (seenNames.has(name)) {
                setUploadError(
                  "Duplicate food detected: Name must be unique for each dish."
                );
                setExcelPreviewData(null);
                setExcelPreviewHeaders([]);
                setIsFileValid(false);
                return;
              }
              seenNames.add(name);
            }
          }

          // Validate every row for vintage column (wine uploads)
          if (activeTab === "wine" && uploadedHeaders.includes("Vintage (Year)")) {
            const vintageIndex = uploadedHeaders.indexOf("Vintage (Year)");
            for (let i = 1; i < json.length; i++) {
              const row = json[i];
              const vintageValue = row[vintageIndex];
              if (
                vintageValue !== undefined &&
                vintageValue !== "" &&
                isNaN(Number(vintageValue) || vintageValue === "NV")
              ) {
                setUploadError("Vintage is required and it must be a number.");
                setExcelPreviewData(null);
                setExcelPreviewHeaders([]);
                setIsFileValid(false);
                return;
              }
            }

            // Unique wine validation
            const producerIdx = uploadedHeaders.indexOf("Producer Name (Winery)");
            const productIdx = uploadedHeaders.indexOf("Product Name (Label Name)");
            // vintageIndex already defined
            const seen = new Set();
            for (let i = 1; i < json.length; i++) {
              const row = json[i];
              const key = `${row[producerIdx]
                ?.toString()
                .trim()
                .toLowerCase()}|${row[productIdx]
                  ?.toString()
                  .trim()
                  .toLowerCase()}|${row[vintageIndex]
                    ?.toString()
                    .trim()
                    .toLowerCase()}`;
              if (seen.has(key)) {
                setUploadError(
                  "Duplicate wine detected: Producer, Product Name, and Vintage must be unique for each wine."
                );
                setExcelPreviewData(null);
                setExcelPreviewHeaders([]);
                setIsFileValid(false);
                return;
              }
              seen.add(key);
            }
          }

          setExcelPreviewHeaders(uploadedHeaders);
          setExcelPreviewData(json.slice(1));
          setUploadError("");
          setIsFileValid(true);
        } else {
          setUploadError("The Excel file is empty.");
          setExcelPreviewData(null);
          setExcelPreviewHeaders([]);
          setIsFileValid(false);
        }
      } catch (error) {
        setUploadError("Error reading or parsing the Excel file.");
        setExcelPreviewData(null);
        setExcelPreviewHeaders([]);
        setIsFileValid(false);
      }
    };

    reader.onerror = () => {
      setUploadError("Error reading the file.");
      setExcelPreviewData(null);
      setExcelPreviewHeaders([]);
      setIsFileValid(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // Tri-state sort handler
  const handleSortChange = (column) => {
    // Reset to first page whenever sorting changes
    setCurrentPage(1);
    console.log(column, 'column');
    console.log(sortColumn, sortDirection)

    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Helper function to get wine name for secondary sorting
  const getWineName = (wine) => {
    const producer = (wine.producer_name || '').toLowerCase();
    const product = (wine.product_name || wine.name || '').toLowerCase();
    return `${producer} ${product}`.trim();
  };

  // Helper function to get dish name
  const getDishName = (dish) => {
    return (dish.name || '').toLowerCase();
  };

  // Sort function for date filter (legacy)
  const sortByDate = (a, b) => {
    if (selectedDateFilter === "Date Added") {
      return new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at);
    } else if (selectedDateFilter === "Last Updated") {
      return new Date(b.updatedAt || b.updated_at) - new Date(a.updatedAt || a.updated_at);
    } else if (selectedDateFilter === "Price") {
      return (b.price || 0) - (a.price || 0);
    }
    return 0;
  };

  // Comprehensive sorting function - updated to support server-side sorting
  const applySorting = (items) => {
    // When backend sorting is active, return items as-is (already sorted by backend)
    //  if (sortColumn && sortDirection) {
    //    return items;
    //  }

    //  // No sorting specified, apply default date-based sort
    //  if (!sortColumn || !sortDirection) {
    //    return items.sort(sortByDate);
    //  }

    return items;

  };

  // Use backend data directly, backend is already filtering
  const filteredItems = applySorting(activeTab === "food" ? dishes : (activeTab === "beer" ? beers : wines));
  console.log(`Render: activeTab=${activeTab}, dishes.length=${dishes.length}, wines.length=${wines.length}, beers.length=${beers.length}, filteredItems.length=${filteredItems.length}, sortColumn=${sortColumn}, sortDirection=${sortDirection}`);

  // Format region data for display
  const formatRegion = (region) => {
    if (!region) return "";
    const parts = [region.country, region.appellation, region.state].filter(
      Boolean
    );
    return parts.join(", ");
  };

  // Format style data for display
  const formatStyle = (style) => {
    if (!style) return "";
    const parts = [
      style.body,
      style.sweetness,
      style.acidity,
      style.tannin,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Format dish details for display
  const formatDishDetails = (dish) => {
    // Flatten dietary_restrictions if it's an object
    let dietaryDisplay = "";
    if (dish.dietary_restrictions) {
      if (Array.isArray(dish.dietary_restrictions)) {
        dietaryDisplay = dish.dietary_restrictions.join(", ");
      } else if (typeof dish.dietary_restrictions === "object") {
        dietaryDisplay = [
          ...(dish.dietary_restrictions.health || []),
          ...(dish.dietary_restrictions.belief || []),
          ...(dish.dietary_restrictions.lifestyle || []),
        ].join(", ");
      }
    }

    let restaurantUUID = null;
    if (dish.restaurant_uuid && typeof dish.restaurant_uuid === "object") {
      restaurantUUID = dish.restaurant_uuid.uuid;
    } else {
      restaurantUUID = dish.restaurant_uuid;
    }

    return {
      ...dish,
      restaurant_uuid: restaurantUUID,
      typeDisplay: Array.isArray(dish.type) ? dish.type.join(", ") : (dish.type || ""),
      // dietaryDisplay,
      temperatureDisplay: dish.temperature || "",
      updatedAt: dish?.updatedAt,
      categoryDisplay: Array.isArray(dish.category) ? dish.category.join(", ") : (dish.category || ""),
      restaurantNameDisplay: dish.restaurantname || "",
    };
  };

  // Format wine details for display
  const formatWineDetails = (wine) => {
    let restaurantUUID = null;
    if (wine.restaurant_uuid && typeof wine.restaurant_uuid === "object") {
      restaurantUUID = wine.restaurant_uuid.uuid;
    } else {
      restaurantUUID = wine.restaurant_uuid;
    }
    return {
      ...wine,
      restaurant_uuid: restaurantUUID,
      restaurantname: wine.restaurantname || "",
      styleDisplay: formatStyle(wine.style),
      producerDisplay: wine.producer_name || "",
      vintageDisplay: wine.vintage,
      updatedAt: wine.updatedAt || "2025-04-11T16:51:03.849Z",
      varietalsDisplay: Array.isArray(wine.varietals) ? wine.varietals.join(", ") : (wine.varietals || ""),
    };
  };

  // Format beer details for display
  const formatBeerDetails = (beer) => {
    let restaurantUUID = null;
    if (beer.restaurant_uuid && typeof beer.restaurant_uuid === "object") {
      restaurantUUID = beer.restaurant_uuid.uuid;
    } else {
      restaurantUUID = beer.restaurant_uuid;
    }
    return {
      ...beer,
      restaurant_uuid: restaurantUUID,
      restaurantname: beer.restaurantname || "",
      styleDisplay: beer.style || "",
      producerDisplay: beer.brewery_name || "",
      updatedAt: beer.updatedAt || "",
    };
  };

  // Format items based on type
  const formattedItems = [];
  filteredItems.forEach((item) => {
    formattedItems.push(
      activeTab === "food" ? formatDishDetails(item) : (activeTab === "beer" ? formatBeerDetails(item) : formatWineDetails(item))
    );
  });


  if (initialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">{error}</div>;
  }


  const handleDropdownSelect = (type, option) => {
    setFilters((prev) => ({
      ...prev,
      [type]: option.value === "all" ? null : option.value,
    }));
  };

  const handleFetchAllItems = async () => {
    const trimmedQuery = (searchQuery || "").trim();
    const restaurantId = (filters?.restaurant || "").trim();
    const dishType = (filters?.category || "").trim();
    const status = filters?.status;
    const btg = wineOffering.btg;
    const btb = wineOffering.btb;

    try {
      if (activeTab === "food") {
        const dishesData = await MenuService.getAllDishes(
          1,
          10000,
          trimmedQuery,
          restaurantId || undefined,
          dishType || undefined,
          status || undefined
        );

        const d1 = dishesData?.dishesWithRestaurant;
        const d2 = dishesData?.dishes;
        const d3 = dishesData?.data;

        let dishesList = [];
        if (Array.isArray(dishesData)) {
          dishesList = dishesData;
        } else if (Array.isArray(d1) && d1.length > 0) {
          dishesList = d1;
        } else if (Array.isArray(d2) && d2.length > 0) {
          dishesList = d2;
        } else if (Array.isArray(d3) && d3.length > 0) {
          dishesList = d3;
        } else {
          dishesList = d1 || d2 || d3 || [];
        }
        return dishesList;
      } else if (activeTab === "beer") {
        const beersData = await MenuService.getAllBeers(
          1,
          10000,
          trimmedQuery,
          restaurantId || undefined,
          dishType || undefined,
          status || undefined,
          beerOffering.draft,
          beerOffering.can,
          beerOffering.bottle
        );

        const b1 = beersData?.beersWithRestaurant;
        const b2 = beersData?.beers;
        const b3 = beersData?.data;

        let beersList = [];
        if (Array.isArray(beersData)) {
          beersList = beersData;
        } else if (Array.isArray(b1) && b1.length > 0) {
          beersList = b1;
        } else if (Array.isArray(b2) && b2.length > 0) {
          beersList = b2;
        } else if (Array.isArray(b3) && b3.length > 0) {
          beersList = b3;
        } else {
          beersList = b1 || b2 || b3 || [];
        }
        return beersList;
      } else {
        const winesData = await MenuService.getAllWines(
          1,
          10000,
          trimmedQuery,
          restaurantId || undefined,
          dishType || undefined,
          status || undefined,
          btg,
          btb
        );

        const w1 = winesData?.winesWithRestaurant;
        const w2 = winesData?.wines;
        const w3 = winesData?.data;

        let winesList = [];
        if (Array.isArray(winesData)) {
          winesList = winesData;
        } else if (Array.isArray(w1) && w1.length > 0) {
          winesList = w1;
        } else if (Array.isArray(w2) && w2.length > 0) {
          winesList = w2;
        } else if (Array.isArray(w3) && w3.length > 0) {
          winesList = w3;
        } else {
          winesList = w1 || w2 || w3 || [];
        }
        return winesList;
      }
    } catch (error) {
      console.error("Failed to fetch all items for export", error);
      return [];
    }
  };

  // Beer Offering toggles (Draft / Can / Bottle), the beer counterpart of Wine's BTG/BTB
  const beerOfferingToggles = (
    <div className="flex bg-white border border-gray-200 rounded-md h-9 px-2 items-center gap-3">
      {[
        { key: "draft", label: "Draft" },
        { key: "can", label: "Can" },
        { key: "bottle", label: "Bottle" },
      ].map(({ key, label }, index) => (
        <div key={key} className="flex items-center gap-3">
          {index > 0 && <div className="w-[1px] h-4 bg-gray-100"></div>}
          <label className="flex items-center gap-1.5 cursor-pointer select-none group">
            <input
              type="checkbox"
              checked={beerOffering[key]}
              onChange={(e) => setBeerOffering({ ...beerOffering, [key]: e.target.checked })}
              className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
            />
            <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900 transition-colors">{label}</span>
          </label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 overflow-auto">
        <main className="p-3">
          <div className="bg-white text-start p-6 rounded-2xl border border-gray-200 shadow-sm mb-8">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Manage Menu</h1>
                <p className="text-gray-500 text-sm font-medium">Review, edit, and monitor all menu items across restaurants.</p>
              </div>
              {userRole !== "employee" && (
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => {
                      setShowBulkUploadModal(true);
                      setSelectedFile(null);
                      setExcelPreviewData(null);
                      setExcelPreviewHeaders([]);
                      setUploadError("");
                      setDragActive(false);
                    }}
                    className="flex justify-center items-center gap-2 px-5 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-all text-sm w-full sm:w-auto h-10 shadow-sm"
                  >
                    <Upload size={16} strokeWidth={2} />
                    <span>Bulk Upload</span>
                  </button>
                  <button
                    onClick={handleAddItem}
                    className="flex justify-center items-center gap-2 px-5 py-2 bg-[#881337] text-white rounded-lg font-semibold hover:bg-[#70102d] transition-all shadow-sm text-sm w-full sm:w-auto h-10"
                  >
                    <Plus size={18} strokeWidth={2.5} />
                    <span>{activeTab === "food" ? "Add Food" : activeTab === "beer" ? "Add Beer" : "Add Wine"}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Tabs and Filters Section */}
            <div className="flex flex-col md:flex-row items-start gap-4 border-t border-gray-100 pt-6">
              {/* Tabs */}
              <div className="flex items-center gap-1 bg-gray-50/50 p-1 rounded-lg border border-gray-100 shrink-0">
                <button
                  onClick={() => handleTabChange("food")}
                  className={`px-4 py-1.5 rounded-md font-semibold text-sm transition-all duration-200 ${activeTab === "food"
                    ? "bg-white text-[#881337] shadow-sm border border-gray-200"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                    }`}
                >
                  Food
                </button>
                <button
                  onClick={() => handleTabChange("wine")}
                  className={`px-2 py-1.5 rounded-md font-semibold text-sm transition-all duration-200 ${activeTab === "wine"
                    ? "bg-white text-[#881337] shadow-sm border border-gray-200"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                    }`}
                >
                  Wine
                </button>
                <button
                  onClick={() => handleTabChange("beer")}
                  className={`px-2 py-1.5 rounded-md font-semibold text-sm transition-all duration-200 ${activeTab === "beer"
                    ? "bg-white text-[#881337] shadow-sm border border-gray-200"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                    }`}
                >
                  Beer
                </button>
              </div>


              {/* Filters & Search */}
              <div className="flex-1 w-full md:w-auto overflow-visible">
                {activeTab === "food" ? (
                  <div className="flex flex-row flex-wrap items-center justify-start gap-2">
                    {userRole !== "employee" && (
                      <>
                        <Dropdown
                          label="All Restaurants"
                          options={filterOptions.restaurant}
                          selectedOption={filters.restaurant}
                          onSelect={(option) => handleDropdownSelect("restaurant", option)}
                          className="min-w-[160px]"
                          buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                          searchable
                          searchPlaceholder="Search restaurant..."
                        />
                        <Dropdown
                          label="All Categories"
                          options={foodCategoriesOptions}
                          selectedOption={filters.category}
                          onSelect={(option) => handleDropdownSelect("category", option)}
                          className="min-w-[140px]"
                          buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                          searchable
                          searchPlaceholder="Search category..."
                        />
                        <Dropdown
                          label="All Status"
                          options={[
                            { value: "all", label: "All Status" },
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" }
                          ]}
                          selectedOption={filters.status || "all"}
                          onSelect={(option) => handleDropdownSelect("status", option)}
                          className="min-w-[110px]"
                          buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                        />
                      </>
                    )}

                    {/* Search */}
                    <div className="relative w-[200px]">
                      <input
                        type="text"
                        placeholder={userRole === "employee" ? "Search by name or category..." : "Search..."}
                        className="w-full h-9 pl-9 pr-10 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#881337]/10 focus:border-[#881337] text-sm"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          onClick={() => handleSearch("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  userRole === "employee" ? (
                    <div className="flex flex-row flex-wrap items-center justify-start gap-2">
                      <Dropdown
                        label={activeTab === "beer" ? "All Styles" : "All Categories"}
                        options={activeTab === "beer" ? beerCategoriesOptions : wineCategoriesOptions}
                        selectedOption={filters.category}
                        onSelect={(option) => handleDropdownSelect("category", option)}
                        className="min-w-[140px]"
                        buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                        searchable
                        searchPlaceholder={activeTab === "beer" ? "Search style..." : "Search category..."}
                      />

                      {/* Wine / Beer Offering toggles */}
                      {activeTab === "beer" ? beerOfferingToggles : (
                      <div className="flex bg-white border border-gray-200 rounded-md h-9 px-2 items-center gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer select-none group">
                          <input
                            type="checkbox"
                            checked={wineOffering.btg}
                            onChange={(e) => setWineOffering({ ...wineOffering, btg: e.target.checked })}
                            className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
                          />
                          <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900 transition-colors">BTG</span>
                        </label>
                        <div className="w-[1px] h-4 bg-gray-100"></div>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none group">
                          <input
                            type="checkbox"
                            checked={wineOffering.btb}
                            onChange={(e) => setWineOffering({ ...wineOffering, btb: e.target.checked })}
                            className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
                          />
                          <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900 transition-colors">BTB</span>
                        </label>
                      </div>
                      )}

                      {/* Search */}
                      <div className="relative w-[200px]">
                        <input
                          type="text"
                          placeholder="Search..."
                          className="w-full h-9 pl-9 pr-10 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#881337]/10 focus:border-[#881337] text-sm"
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                        />
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        {searchQuery && (
                          <button
                            onClick={() => handleSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Clear All Filters */}
                      {(filters.category !== null || wineOffering.btg || wineOffering.btb || beerOffering.draft || beerOffering.can || beerOffering.bottle || searchQuery) && (
                        <button
                          onClick={() => {
                            setFilters(prev => ({
                              ...prev,
                              category: null,
                            }));
                            setWineOffering({ btg: false, btb: false });
                            setBeerOffering({ draft: false, can: false, bottle: false });
                            handleSearch("");
                          }}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-row flex-wrap items-center justify-start gap-2">
                      <Dropdown
                        label="All Restaurants"
                        options={filterOptions.restaurant}
                        selectedOption={filters.restaurant}
                        onSelect={(option) => handleDropdownSelect("restaurant", option)}
                        className="min-w-[160px]"
                        buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                        searchable
                        searchPlaceholder="Search restaurant..."
                      />

                      <Dropdown
                        label={activeTab === "beer" ? "All Styles" : "All Categories"}
                        options={activeTab === "beer" ? beerCategoriesOptions : wineCategoriesOptions}
                        selectedOption={filters.category}
                        onSelect={(option) => handleDropdownSelect("category", option)}
                        className="min-w-[140px]"
                        buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                        searchable
                        searchPlaceholder={activeTab === "beer" ? "Search style..." : "Search category..."}
                      />

                      <Dropdown
                        label="All Status"
                        options={[
                          { value: "all", label: "All Status" },
                          { value: "active", label: "Active" },
                          { value: "inactive", label: "Inactive" }
                        ]}
                        selectedOption={filters.status || "all"}
                        onSelect={(option) => handleDropdownSelect("status", option)}
                        className="min-w-[110px]"
                        buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
                      />

                      {/* Wine / Beer Offering toggles */}
                      {activeTab === "beer" ? beerOfferingToggles : (
                      <div className="flex bg-white border border-gray-200 rounded-md h-9 px-2 items-center gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer select-none group">
                          <input
                            type="checkbox"
                            checked={wineOffering.btg}
                            onChange={(e) => setWineOffering({ ...wineOffering, btg: e.target.checked })}
                            className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
                          />
                          <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900 transition-colors">BTG</span>
                        </label>
                        <div className="w-[1px] h-4 bg-gray-100"></div>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none group">
                          <input
                            type="checkbox"
                            checked={wineOffering.btb}
                            onChange={(e) => setWineOffering({ ...wineOffering, btb: e.target.checked })}
                            className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
                          />
                          <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900 transition-colors">BTB</span>
                        </label>
                      </div>
                      )}

                      {/* Search */}
                      <div className="relative w-[200px]">
                        <input
                          type="text"
                          placeholder="Search..."
                          className="w-full h-9 pl-9 pr-10 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#881337]/10 focus:border-[#881337] text-sm"
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                        />
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        {searchQuery && (
                          <button
                            onClick={() => handleSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>

            </div>
          </div>

          {/* Menu Items Table */}
          <div className="overflow-x-auto">
            <MenuItemsTable
              items={formattedItems}
              onEditItem={handleEditItem}
              activeTab={activeTab}
              previousTable={false}
              loading={tableLoading}
              error={error}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              restaurantUuid={filters.restaurant}
              restaurantName={currentRestaurantName}
              validDishTypes={validDishTypes}
              onFetchAllItems={handleFetchAllItems}
              onRefresh={handleRefresh}
              sortBy={sortColumn}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          </div>


        </main>
      </div>

      {/* Edit Panel */}
      {showEditPanel &&
        (activeTab === "food" ? (
          <MenuItemEditPanel
            item={selectedMenuItem}
            onClose={handleCloseEditPanel}
            onSave={handleSaveItem}
            isSaving={isSaving}
            validDishTypes={validDishTypes}
          />
        ) : activeTab === "beer" ? (
          <BeerItemEditPanel
            item={selectedMenuItem}
            onClose={handleCloseEditPanel}
            onSave={handleSaveItem}
            isSaving={isSaving}
            validBeerTypes={validBeerTypes.length ? validBeerTypes : CORE_STYLES}
            restaurantScope={filters.restaurant}
            restaurantScopeName={currentRestaurantName}
          />
        ) : (
          <WineItemEditPanel
            item={selectedMenuItem}
            onClose={handleCloseEditPanel}
            onSave={handleSaveItem}
            isSaving={isSaving}
            validWineTypes={validWineTypes}
          />
        ))}

      {/* Analytics Modal */}
      {/* <MenuAnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
      /> */}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black opacity-30"
            onClick={() => {
              setShowBulkUploadModal(false);
              setExcelPreviewData([]);
              setExcelPreviewHeaders([]);
              setSelectedFile(null);
              setUploadError("");
              setSelectedRestaurant("");
            }}
          ></div>
          <div className="fixed inset-y-0 right-0 w-full md:w-1/2 lg:w-[50%] bg-white border-l border-gray-200 shadow-lg z-50 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-textcolor">
                Bulk Upload {activeTab === "food" ? "Dishes" : activeTab === "beer" ? "Beers" : "Wines"}
              </h2>
              <button
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setExcelPreviewData([]);
                  setExcelPreviewHeaders([]);
                  setSelectedFile(null);
                  setUploadError("");
                  setSelectedRestaurant("");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-textcolor"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Restaurant
                  </label>
                  <Dropdown
                    label="Select Restaurant"
                    options={restaurants.map(r => ({
                      value: r.uuid || r.id,
                      label: r.name
                    }))}
                    selectedOption={selectedRestaurant}
                    onSelect={(option) => setSelectedRestaurant(option.value)}
                    className="w-full mb-4"
                    buttonClassName="h-10 text-sm font-medium border-gray-300 rounded-md"
                    searchable
                    searchPlaceholder="Search restaurant..."
                  />
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">
                    Upload Excel File
                  </h3>
                  <p className="text-sm text-gray-500">
                    Upload an Excel file containing your{" "}
                    {activeTab === "food" ? "dishes" : activeTab === "beer" ? "beers" : "wines"}. Make sure to
                    follow the correct format.
                  </p>
                </div>

                <div
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md ${dragActive ? "bg-gray-100" : ""
                    }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                      >
                        <span>
                          {selectedFile ? selectedFile.name : "Upload a file"}
                        </span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept=".xlsx, .xls, .csv"
                          className="sr-only"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      (XLSX, XLS & CSV) up to 10MB
                    </p>
                  </div>
                </div>
              </div>
              {!selectedFile && excelPreviewDataSample && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-2">
                    Expected Format Sample
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Your Excel file should follow this format:
                  </p>
                  <div className="overflow-x-auto max-h-96 border border-gray-300 rounded-md">
                    <table
                      className="min-w-full"
                      style={{ borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          {excelPreviewHeadersSample.map((header, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 text-center"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelPreviewDataSample.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {excelPreviewHeadersSample.map(
                              (header, cellIndex) => (
                                <td
                                  key={cellIndex}
                                  className="px-3 py-2 text-sm text-gray-800 border border-gray-300 text-center"
                                >
                                  {row[cellIndex] !== undefined
                                    ? row[cellIndex]
                                    : ""}
                                </td>
                              )
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {excelPreviewData && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-2">
                    Excel Data Preview
                  </h3>
                  <div className="overflow-x-auto max-h-96">
                    <table
                      className="min-w-full border border-gray-400"
                      style={{ borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          {excelPreviewHeaders.map((header, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-400 text-center"
                              style={{
                                border: "1px solid #ccc",
                                background: "#f9f9f9",
                              }}
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelPreviewData.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {excelPreviewHeaders.map((header, cellIndex) => (
                              <td
                                key={cellIndex}
                                className="px-3 py-2 text-sm text-gray-800 border border-gray-400 text-center"
                                style={{
                                  border: "1px solid #ccc",
                                  background: "#fff",
                                }}
                              >
                                {row[cellIndex] !== undefined
                                  ? row[cellIndex]
                                  : ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {uploadError && (
                <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
                  {uploadError}
                </div>
              )}

              <div className="flex justify-between items-center space-x-2 pt-4 border-t border-gray-200">
                <a
                  href={activeTab === "food" ? "./food.xlsx" : activeTab === "beer" ? "./beer.xlsx" : "./wine.xlsx"}
                  download
                  className="inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 bg-textcolor text-white hover:bg-gray-400 px-4 py-2 text-base"
                  style={{ textDecoration: "none" }}
                >
                  Download Template
                </a>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowBulkUploadModal(false);
                      setExcelPreviewData(null);
                      setExcelPreviewHeaders([]);
                      setSelectedFile(null);
                      setSelectedRestaurant("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleBulkUpload}
                    disabled={
                      !selectedFile ||
                      isUploading ||
                      !selectedRestaurant ||
                      !isFileValid
                    }
                  >
                    {isUploading
                      ? "Uploading..."
                      : `Upload ${activeTab === "food" ? "Dishes" : activeTab === "beer" ? "Beers" : "Wines"}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {saveError && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">Error</h2>
            <p className="mb-6">{saveError}</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => setSaveError("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
